import { useState } from "react";
import { useLocation } from "wouter";
import {
  SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useCreateLead, useConvertLead, useListInventoryPlans } from "@workspace/api-client-react";
import { toast } from "sonner";
import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

interface Props { onClose: () => void; }

export function WalkInWizard({ onClose }: Props) {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  // Phase 10 RET10-01 — planId replaces memberSubtype as the canonical classification.
  const [planId, setPlanId] = useState<number | null>(null);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [gstin, setGstin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const createLead = useCreateLead();
  const convertLead = useConvertLead();
  const { data: plans } = useListInventoryPlans();
  const activePlans = (plans ?? []).filter((p) => p.status === "Active");
  const selectedPlan = activePlans.find((p) => p.id === planId) ?? null;

  // Phase 10 — memberCategory now derives from plan.cadence (was hardcoded Non_Recurring per D-17).
  // Walk-in now supports both recurring and non-recurring plans through the plan picker.
  const memberCategory = selectedPlan?.cadence === "Recurring" ? "Recurring" as const : "Non_Recurring" as const;

  // Validation
  const nameOk = name.trim().length >= 2;
  const mobileOk = /^[6-9]\d{9}$/.test(mobile.trim());
  const gstinOk = !gstin || GSTIN_REGEX.test(gstin.trim());
  const planOk = planId != null;
  const formValid = nameOk && mobileOk && gstinOk && planOk;

  async function onSubmit() {
    setSubmitting(true);
    try {
      const lead = await createLead.mutateAsync({
        data: {
          customerName: name.trim(),
          mobileNumber: mobile.trim(),
          source: "walk_in",  // RESEARCH key finding 2 — lowercase matches existing enum
          spaceType: "day_pass",
          stage: "closed_won",  // mark closed_won so convert-lead's stage check passes
          // Required fields — sensible defaults for walk-in (no prior sales conversation)
          seatRangeMin: 1,
          seatRangeMax: 1,
          urgency: "immediate",
          location: "on_site",
          spaceId: user?.spaceId ?? 0,
        },
      });
      // Phase 23-04: body reshaped to ConvertLeadToAccountBody.
      // Server defaults gstTreatment=unregistered_b2c + placeOfSupply=07 for walk-in.
      const result = await convertLead.mutateAsync({
        data: {
          legalName: name.trim(),
          fullName: name.trim(),
          phone: mobile.trim(),
          planId: planId!,
          startDate: new Date().toISOString(),
          gstin: gstin.trim() || null,
          gstTreatment: gstin.trim() ? ("registered_b2b" as const) : ("unregistered_b2c" as const),
          placeOfSupply: "07",
          // billingCycleDay only relevant for Recurring; default to 1 if Recurring, null otherwise.
          billingCycleDay: memberCategory === "Recurring" ? 1 : null,
        },
      });
      toast.success("Walk-in created. Complete the booking details below.");
      onClose();
      navigate(`/booking/new?accountId=${result.accountId}`);
    } catch (err) {
      const msg = (err as Error)?.message ?? "Unknown error";
      toast.error(`Walk-in failed: ${msg}. Try again.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SheetContent side="right" className="sm:max-w-md">
      <SheetHeader>
        <SheetTitle>Walk-in Member</SheetTitle>
        <SheetDescription>
          Quick walk-in over the 9 canonical plans. Creates a lead with source walk_in,
          then immediately converts to a member with the selected plan.
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label>Name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
          />
        </div>
        <div>
          <Label>Mobile</Label>
          <Input
            type="tel"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="10-digit mobile"
          />
          {!mobileOk && mobile && (
            <p className="text-xs text-destructive mt-1">Enter a valid 10-digit mobile number.</p>
          )}
        </div>
        <div>
          <Label>Plan</Label>
          {/* Phase 10 RET10-01 — Combobox over 9 canonical plans, replaces memberSubtype Select.
              Cadence pill on the right shows the plan's billing model. */}
          <Popover open={planPickerOpen} onOpenChange={setPlanPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={planPickerOpen}
                className={cn("w-full justify-between font-normal", !selectedPlan && "text-muted-foreground")}
              >
                <span className="truncate">
                  {selectedPlan
                    ? `${selectedPlan.planName} (${selectedPlan.cadence === "Recurring" ? "Recurring" : "Non-Recurring"})`
                    : "Choose a plan…"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search plans…" />
                <CommandList className="max-h-[260px]">
                  <CommandEmpty>No plan found.</CommandEmpty>
                  <CommandGroup>
                    {activePlans.map((p) => {
                      const isSelected = planId === p.id;
                      return (
                        <CommandItem
                          key={p.id}
                          value={p.planName}
                          onSelect={() => {
                            setPlanId(p.id);
                            setPlanPickerOpen(false);
                          }}
                          className={cn(
                            isSelected && "bg-primary/10 text-primary font-medium aria-selected:bg-primary/15",
                          )}
                        >
                          <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                          <span className="flex-1 truncate">{p.planName.replace(/_/g, " ")}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({p.cadence === "Recurring" ? "Recurring" : "Non-Recurring"})
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {selectedPlan && (
            <p className="text-xs text-muted-foreground mt-1">
              {selectedPlan.cadence === "Recurring" ? "Recurring member" : "Non-recurring member"}
            </p>
          )}
        </div>
        <div>
          <Label>GSTIN (optional)</Label>
          <Input
            value={gstin}
            onChange={(e) => setGstin(e.target.value)}
            placeholder="22AAAAA0000A1Z5"
          />
          {!gstinOk && gstin && (
            <p className="text-xs text-destructive mt-1">Invalid GSTIN format. Leave blank if not applicable.</p>
          )}
        </div>
      </div>
      <SheetFooter>
        <Button variant="outline" onClick={onClose}>Discard</Button>
        <Button onClick={onSubmit} disabled={!formValid || submitting}>
          {submitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</>
          ) : (
            "Save & Open Booking Form"
          )}
        </Button>
      </SheetFooter>
    </SheetContent>
  );
}
